import { useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete } from '@forwardflow/ui-v2'
import { CloseIcon, SearchMagnifyingGlassIcon } from '@forwardflow/icons'
import { useFilterOptionsSource } from '../context/FilterOptions.context'

function useDebounce<T>(value: T, delay: number): T {
   const [debouncedValue, setDebouncedValue] = useState<T>(value)

   useEffect(() => {
      const timeout = setTimeout(() => {
         setDebouncedValue(value)
      }, delay)

      return () => clearTimeout(timeout)
   }, [value, delay])

   return debouncedValue
}

interface InquiredPropertyInputProps {
   value: string[]
   onChange: (values: string[]) => void
   placeholder?: string
}

export const InquiredPropertyInput = ({
   value,
   onChange,
   placeholder = 'Search and add property addresses',
}: InquiredPropertyInputProps) => {
   const [inputValue, setInputValue] = useState('')
   const [suggestions, setSuggestions] = useState<string[]>([])
   const [isLoading, setIsLoading] = useState(false)
   const debouncedInput = useDebounce(inputValue, 300)
   const isSelectingRef = useRef(false)
   const { searchProperties } = useFilterOptionsSource()

   useEffect(() => {
      if (!debouncedInput.trim()) {
         setSuggestions([])
         return
      }

      let cancelled = false

      const fetchSuggestions = async () => {
         try {
            setIsLoading(true)
            const nextSuggestions = await searchProperties(
               debouncedInput,
               10
            )

            if (cancelled) return

            setSuggestions([...new Set(nextSuggestions.filter(Boolean))])
         } catch {
            if (!cancelled) {
               setSuggestions([])
            }
         } finally {
            if (!cancelled) {
               setIsLoading(false)
            }
         }
      }

      fetchSuggestions()

      return () => {
         cancelled = true
      }
   }, [debouncedInput, searchProperties])

   const normalizedExistingValues = useMemo(
      () => new Set(value.map((item) => item.trim().toLowerCase())),
      [value]
   )

   const addValue = (nextValue: string) => {
      const trimmedValue = nextValue.trim()
      if (!trimmedValue) return

      if (normalizedExistingValues.has(trimmedValue.toLowerCase())) {
         setInputValue('')
         setSuggestions([])
         return
      }

      onChange([...value, trimmedValue])
      setInputValue('')
      setSuggestions([])
   }

   const handleInputChange = (nextValue: string) => {
      setInputValue(nextValue)
      if (isSelectingRef.current) {
         isSelectingRef.current = false
      }
   }

   const handleOptionSubmit = (selectedValue: string) => {
      isSelectingRef.current = true
      addValue(selectedValue)
   }

   const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return

      event.preventDefault()

      if (!inputValue.trim()) return

      addValue(inputValue)
   }

   const handleRemove = (address: string) => {
      onChange(value.filter((item) => item !== address))
   }

   return (
      <div className="space-y-2">
         <Autocomplete
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onOptionSubmit={handleOptionSubmit}
            onKeyDown={handleKeyDown}
            data={suggestions}
            filter={({ options }) => options}
            leftSection={<SearchMagnifyingGlassIcon width={16} />}
            shouldLeftSectionBeFocused
            rightSection={isLoading ? '...' : undefined}
         />
         {value.length > 0 && (
            <div className="flex flex-wrap gap-2">
               {value.map((address) => (
                  <div
                     key={address}
                     className="flex items-center gap-2 rounded-md border border-[#A684FF]/30 bg-[#A684FF]/10 py-1.5 px-3"
                  >
                     <p className="text-white text-xs font-medium">
                        {address}
                     </p>
                     <CloseIcon
                        width={12}
                        height={12}
                        className="cursor-pointer shrink-0"
                        onClick={() => handleRemove(address)}
                     />
                  </div>
               ))}
            </div>
         )}
      </div>
   )
}
